(function exposeTitanScanAI(root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TitanScanAI = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createTitanScanAI(root) {
  'use strict';

  const scanLogic = root && root.TitanScanLogic
    ? root.TitanScanLogic
    : typeof module === 'object' && module.exports
      ? require('./tps-v2-logic.js')
      : null;
  const API_URL = 'https://pc-ai-relay.vercel.app/api/chat';
  const MODEL = 'gpt-5.6-luna';
  const SIGNING_SALT = 'T1t4nG#2025xQ';
  const REQUEST_TIMEOUT_MS = 25000;
  const HOURLY_LIMIT = 10;
  const HOURLY_WINDOW_MS = 60 * 60 * 1000;
  const HOURLY_STORAGE_KEY = 'wz_diag_hour_v1';
  const GPU_CATALOG = Object.freeze([
    { pattern: /RTX\s*5080/i, score: 33000, vramGb: 16 },
    { pattern: /RTX\s*5070\s*Ti/i, score: 27700, vramGb: 16 },
    { pattern: /RTX\s*5070/i, score: 22500, vramGb: 12 },
    { pattern: /RTX\s*5060\s*Ti/i, score: 15200, vramGb: 8 },
    { pattern: /RTX\s*5060/i, score: 13900, vramGb: 8 },
    { pattern: /RTX\s*4060/i, score: 10500, vramGb: 8 },
    { pattern: /RTX\s*3050/i, score: 6278, vramGb: 6 },
  ]);
  const GAME_SCORE_FLOORS = Object.freeze([0, 6000, 10000, 13000, 22000, 27000, 33000]);

  function isObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function averageReaction(samples) {
    const valid = Array.isArray(samples)
      ? samples.map(Number).filter((value) => Number.isFinite(value) && value > 0 && value <= 1500)
      : [];
    return valid.length
      ? Math.round(valid.reduce((sum, value) => sum + value, 0) / valid.length)
      : 0;
  }

  function gpuDetails(gpuName) {
    const name = typeof gpuName === 'string' ? gpuName : '';
    const matched = GPU_CATALOG.find((entry) => entry.pattern.test(name));
    if (!matched) return null;
    const explicitVram = name.match(/(\d{1,2})\s*GB/i);
    return {
      score: matched.score,
      vramGb: explicitVram ? Number(explicitVram[1]) : matched.vramGb,
    };
  }

  function buildProfile(state, data) {
    if (!isObject(state) || !isObject(state.answers) || !isObject(state.metrics)) {
      throw new TypeError('PLAYER SCAN state is incomplete.');
    }
    const answers = state.answers;
    const game = data.games.find((item) => item.id === answers.game);
    if (!game) throw new TypeError('Selected game is unavailable.');

    const seriousStreaming = answers.streaming === 'serious';
    const quality = answers.priority === 'quality';
    const resolution = quality && game.load === 'ultra' ? '4K' : quality ? 'WQHD' : 'FHD';
    let consultationFloor = 7000;
    if (resolution === '4K') consultationFloor = seriousStreaming ? 27000 : 22000;
    else if (resolution === 'WQHD') consultationFloor = seriousStreaming ? 18000 : 14000;
    else if (answers.style === 'competitive' || answers.monitorHz >= 240) consultationFloor = 13000;
    else if (answers.priority === 'fps') consultationFloor = 11000;
    else if (quality) consultationFloor = 10000;
    if (resolution === 'FHD' && seriousStreaming) consultationFloor = Math.max(consultationFloor, 12500);

    const gameFloor = GAME_SCORE_FLOORS[game.baseGpuRank] || 0;
    const inputScore = Number(state.metrics.inputScore) || 0;
    const accuracy = Number(state.metrics.accuracy) || 0;
    const reactionAverage = averageReaction(state.metrics.reactions);
    const requiredGpuRank = scanLogic && typeof scanLogic.determineGpuRank === 'function'
      ? scanLogic.determineGpuRank(answers, state.metrics, game)
      : game.baseGpuRank;
    const measuredTier = inputScore >= 80 && accuracy >= 80 && reactionAverage > 0 && reactionAverage <= 230
      ? 'high'
      : inputScore >= 55 && accuracy >= 65 && reactionAverage > 0 && reactionAverage <= 320
        ? 'standard'
        : 'developing';
    return Object.freeze({
      gameId: game.id,
      gameName: game.name,
      gameLoad: game.load,
      style: answers.style,
      monitorHz: answers.monitorHz,
      resolution,
      priority: answers.priority,
      streaming: seriousStreaming ? 'yes' : answers.streaming === 'occasional' ? 'sometimes' : 'no',
      budget: answers.budgetUnlimited ? Number.POSITIVE_INFINITY : answers.budget,
      budgetUnlimited: answers.budgetUnlimited === true,
      inputLabel: state.metrics.mode === 'touch' ? 'TAP SCORE' : 'AIM SCORE',
      deviceLabel: state.metrics.mode === 'touch' ? 'スマホ・タブレット / タップ' : 'PC / マウス',
      inputScore,
      hits: Number(state.metrics.hits) || 0,
      misses: Number(state.metrics.misses) || 0,
      accuracy,
      averageInterval: Number(state.metrics.averageInterval) || 0,
      fastestInterval: Number(state.metrics.fastestInterval) || 0,
      reactionAverage,
      measuredTier,
      requiredGpuRank,
      minimumGpuScore: Math.max(gameFloor, consultationFloor),
      minimumVramGb: resolution === '4K' ? 12 : 8,
    });
  }

  function productCandidate(product) {
    if (!isObject(product) || typeof product.id !== 'string' || product.available === false) return null;
    const details = gpuDetails(product.gpu);
    if (!details || !Number.isFinite(product.priceMin)) return null;
    return Object.freeze({ ...product, gpuScore: details.score, vramGb: details.vramGb });
  }

  function eligibleProducts(profile, products, options = {}) {
    const allowOverBudget = options.allowOverBudget === true;
    return (Array.isArray(products) ? products : [])
      .map(productCandidate)
      .filter(Boolean)
      .filter(
        (product) =>
          product.gpuScore >= profile.minimumGpuScore &&
          product.gpuRank >= profile.requiredGpuRank &&
          product.vramGb >= profile.minimumVramGb &&
          (allowOverBudget || profile.budgetUnlimited || product.priceMin <= profile.budget),
      )
      .sort((left, right) => left.priceMin - right.priceMin || left.gpuScore - right.gpuScore);
  }

  function yen(value) {
    return `${Math.round(value).toLocaleString('ja-JP')}円`;
  }

  function buildPrompt(profile, candidates) {
    const budget = profile.budgetUnlimited ? '上限なし' : `${yen(profile.budget)}以下`;
    const candidateLines = candidates.map(
      (product) =>
        `- ID:${product.id} / ${product.name} / GPU:${product.gpu} (${product.gpuScore}pt・VRAM ${product.vramGb}GB) / CPU:${product.cpu} / メモリ:${product.memory}GB / ${yen(product.priceMin)}`,
    );
    return [
      '【TITAN PLAYER SCANからの構成選定】追加質問は不要です。',
      `ゲーム: ${profile.gameName}`,
      `プレイスタイル: ${profile.style}`,
      `モニター: ${profile.monitorHz}Hz`,
      `画面方針: ${profile.resolution}・${profile.priority}`,
      `配信・編集: ${profile.streaming}`,
      `予算: ${budget}`,
      `使用端末・操作: ${profile.deviceLabel}`,
      `${profile.inputLabel}: ${profile.inputScore}`,
      `${profile.inputLabel === 'TAP SCORE' ? 'タップ命中数' : '命中数'}: ${profile.hits}`,
      `${profile.inputLabel === 'TAP SCORE' ? '誤タップ数' : 'ミス数'}: ${profile.misses}`,
      `命中率: ${profile.accuracy}%`,
      ...(profile.inputLabel === 'TAP SCORE'
        ? [`平均タップ間隔: ${profile.averageInterval || '未計測'}ms`, `最速タップ間隔: ${profile.fastestInterval || '未計測'}ms`]
        : []),
      `平均反応速度: ${profile.reactionAverage}ms`,
      `実測評価: ${profile.measuredTier}`,
      `最低条件: GPUランク${profile.requiredGpuRank}以上・Time Spy ${profile.minimumGpuScore}pt以上・VRAM ${profile.minimumVramGb}GB以上`,
      '',
      '【選択可能な実商品】',
      ...candidateLines,
      '',
      '商品IDは候補一覧から1つだけ選んでください。候補外の商品・GPU・価格を作らないでください。',
      'ゲーム負荷、モニターHz、画質、配信、予算を優先してください。',
      '実測結果も選定に反映し、高スコアかつ競技・高Hzなら、平均FPSだけでなくCPU性能と最低FPSの安定性を優先してください。',
      '配信ありで実測評価が高い場合は、CPUとメモリの余力も重視してください。低スコアでもゲーム負荷の最低条件は下げないでください。',
      'JSONのみ: {"productId":"候補の商品ID","reason":"選定理由を60文字以内"}',
    ].join('\n');
  }

  function parseJson(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      const match = String(text || '').match(/\{[\s\S]*?\}/);
      if (!match) return null;
      try {
        return JSON.parse(match[0]);
      } catch (nestedError) {
        return null;
      }
    }
  }

  function parseSelection(text, candidates) {
    const parsed = parseJson(text);
    if (!isObject(parsed) || typeof parsed.productId !== 'string') return null;
    if (!candidates.some((product) => product.id === parsed.productId)) return null;
    const reason = typeof parsed.reason === 'string'
      ? parsed.reason.replace(/[<>]/g, '').trim().slice(0, 180)
      : '';
    return Object.freeze({ productId: parsed.productId, reason });
  }

  function djb2(value) {
    let hash = 5381;
    for (let index = 0; index < value.length; index += 1) {
      hash = ((hash << 5) + hash + value.charCodeAt(index)) >>> 0;
    }
    return hash.toString(36);
  }

  function signature() {
    const timestamp = Math.floor(Date.now() / 1000);
    return { _t: timestamp, _k: djb2(`${SIGNING_SALT}:${timestamp}`) };
  }

  function sessionId() {
    try {
      const key = 'tps-v2-ai-session';
      const stored = root?.localStorage?.getItem(key);
      if (stored) return stored;
      const created = `tps_${Math.random().toString(36).slice(2)}${Date.now()}`;
      root?.localStorage?.setItem(key, created);
      return created;
    } catch (error) {
      return `tps_${Date.now()}`;
    }
  }

  function recordWithinLimit() {
    try {
      const storage = root?.localStorage;
      if (!storage) return true;
      const parsed = JSON.parse(storage.getItem(HOURLY_STORAGE_KEY) || '{"ts":[]}');
      const now = Date.now();
      const recent = Array.isArray(parsed.ts)
        ? parsed.ts.filter((value) => Number.isFinite(value) && now - value < HOURLY_WINDOW_MS)
        : [];
      if (recent.length >= HOURLY_LIMIT) return false;
      storage.setItem(HOURLY_STORAGE_KEY, JSON.stringify({ ts: [...recent, now] }));
      return true;
    } catch (error) {
      return true;
    }
  }

  function responseText(payload) {
    if (!isObject(payload)) return '';
    return ['answer', 'reply', 'message', 'text']
      .map((key) => payload[key])
      .find((value) => typeof value === 'string' && value.trim()) || '';
  }

  function wait(milliseconds, signal) {
    if (milliseconds <= 0) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, milliseconds);
      signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    });
  }

  async function recommend(state, data, options = {}) {
    const profile = buildProfile(state, data);
    let candidates = eligibleProducts(profile, data.products);
    if (!candidates.length) candidates = eligibleProducts(profile, data.products, { allowOverBudget: true });
    if (!candidates.length) return null;

    const testProductId = options.testProductId;
    if (typeof testProductId === 'string') {
      return candidates.some((product) => product.id === testProductId)
        ? Object.freeze({ productId: testProductId, reason: 'テスト選定' })
        : null;
    }

    const fetchImpl = options.fetchImpl || root?.fetch;
    if (typeof fetchImpl !== 'function') return null;
    if (!recordWithinLimit()) return null;
    const controller = new AbortController();
    const externalSignal = options.signal;
    const abort = () => controller.abort();
    externalSignal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, REQUEST_TIMEOUT_MS);
    const startedAt = Date.now();
    try {
      options.onStatus?.('AI相談と同じ性能基準で照合しています');
      const signed = signature();
      const response = await fetchImpl(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: buildPrompt(profile, candidates),
          sessionId: sessionId(),
          history: [],
          model: MODEL,
          source: 'player-scan',
          _t: signed._t,
          _k: signed._k,
        }),
        signal: controller.signal,
      });
      if (!response.ok) return null;
      options.onStatus?.('AIの選定結果を確認しています');
      const payload = await response.json();
      const minimumDelay = Math.max(0, Number(options.minimumDelayMs) || 0);
      await wait(Math.max(0, minimumDelay - (Date.now() - startedAt)), controller.signal);
      return parseSelection(responseText(payload), candidates);
    } catch (error) {
      if (externalSignal?.aborted) throw error;
      return null;
    } finally {
      clearTimeout(timeout);
      externalSignal?.removeEventListener('abort', abort);
    }
  }

  return Object.freeze({ buildProfile, eligibleProducts, buildPrompt, parseSelection, recommend });
});
